import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AgremoAdminService } from '../../core/services/agremo-admin.service';
import { ConfirmationModalComponent } from '../../shared/components/confirmation-modal.component';
import {
  AgremoCreditBlock,
  AgremoCreditType,
  ACRES_PER_HECTARE,
  AgremoUsageStats,
  AgremoHealthStatus,
  AgremoMapStatusCode,
  AgremoCameraType,
  AgremoLayerType,
  AgremoLayerStatusCode,
  AgremoAnalysisTypeCodebook,
  AgremoProcessPendingResult
} from '../../core/models/agremo.model';

/**
 * Agremo Credits Management Component
 *
 * Admin page for managing Agremo credit blocks, viewing usage statistics,
 * monitoring Agremo API health, and processing pending analyses.
 * Follows the admin chemicals list pattern.
 */
@Component({
  selector: 'app-agremo-credits',
  standalone: true,
  imports: [CommonModule, FormsModule, ConfirmationModalComponent],
  templateUrl: './agremo-credits.component.html',
  styleUrl: './agremo-credits.component.scss'
})
export class AgremoCreditsComponent implements OnInit {
  /**
   * Exposed for templates that need to convert between acres and hectares
   * inline (e.g., the live unit-preview under the add-credit form). Keeps
   * the conversion constant single-sourced from the model — DON'T inline
   * `2.47105` literals in the HTML.
   */
  readonly ACRES_PER_HECTARE = ACRES_PER_HECTARE;

  // Credit blocks
  activeCredits: AgremoCreditBlock[] = [];
  archivedCredits: AgremoCreditBlock[] = [];
  showArchived = false;

  // Usage statistics
  usageStats: AgremoUsageStats | null = null;

  // Health status
  healthStatus: AgremoHealthStatus | null = null;
  healthLoading = true;

  // Codebooks
  mapStatuses: AgremoMapStatusCode[] = [];
  cameraTypes: AgremoCameraType[] = [];
  layerTypes: AgremoLayerType[] = [];
  layerStatuses: AgremoLayerStatusCode[] = [];
  analysisTypes: AgremoAnalysisTypeCodebook[] = [];
  codebooksLoading = false;
  codebooksError = '';

  // Loading states
  loading = true;
  usageLoading = true;
  errorMessage = '';
  successMessage = '';

  // New credit block form (2026-05-20 multi-unit design)
  // Admin picks a unit (acres or hectares) and enters the amount in that
  // unit. Server stores the value in the block's native unit; conversion
  // to acres happens at consume time. `analysis` is intentionally not in
  // the dropdown — those rows are read-only legacy data.
  showAddForm = false;
  newCreditAmount: number | null = null;
  newCreditType: AgremoCreditType = 'acres';
  newCreditExpiration = '';
  addingCredit = false;

  // Per-unit availability totals (populated from the credit list response)
  availableAcres = 0;
  availableHectares = 0;
  availableAnalyses = 0;

  // Process pending
  processingPending = false;

  // Confirmation modal
  showConfirmModal = false;
  confirmModalProcessing = false;
  archiveTargetId: string | null = null;
  archiveTargetCredits: AgremoCreditBlock | null = null;

  // Process pending confirmation
  showProcessConfirmModal = false;

  constructor(
    private agremoService: AgremoAdminService
  ) {}

  ngOnInit(): void {
    this.loadHealth();
    this.loadCodebooks();
    this.loadCredits();
    this.loadUsageStats();
  }

  // ============================================
  // HEALTH CHECK
  // ============================================

  loadHealth(): void {
    this.healthLoading = true;
    this.agremoService.checkHealth().subscribe({
      next: (status) => {
        this.healthStatus = status;
        this.healthLoading = false;
      },
      error: (error) => {
        console.error('Failed to check Agremo health:', error);
        this.healthStatus = {
          connected: false,
          error: 'Failed to check connection',
          checkedAt: new Date().toISOString()
        };
        this.healthLoading = false;
      }
    });
  }

  // ============================================
  // AGREMO CODEBOOKS
  // ============================================

  loadCodebooks(): void {
    this.codebooksLoading = true;
    this.codebooksError = '';

    let completed = 0;
    const total = 5;
    const checkDone = () => {
      completed++;
      if (completed >= total) {
        this.codebooksLoading = false;
      }
    };

    this.agremoService.getMapStatuses().subscribe({
      next: (statuses) => { this.mapStatuses = statuses; checkDone(); },
      error: (error) => { console.error('Failed to load map statuses:', error); this.codebooksError = 'Failed to load codebooks'; checkDone(); }
    });

    this.agremoService.getCameraTypes().subscribe({
      next: (cameras) => { this.cameraTypes = cameras; checkDone(); },
      error: (error) => { console.error('Failed to load camera types:', error); this.codebooksError = 'Failed to load codebooks'; checkDone(); }
    });

    this.agremoService.getLayerTypes().subscribe({
      next: (types) => { this.layerTypes = types; checkDone(); },
      error: (error) => { console.error('Failed to load layer types:', error); this.codebooksError = 'Failed to load codebooks'; checkDone(); }
    });

    this.agremoService.getLayerStatuses().subscribe({
      next: (statuses) => { this.layerStatuses = statuses; checkDone(); },
      error: (error) => { console.error('Failed to load layer statuses:', error); this.codebooksError = 'Failed to load codebooks'; checkDone(); }
    });

    this.agremoService.getAnalysisTypes().subscribe({
      next: (types) => { this.analysisTypes = types; checkDone(); },
      error: (error) => { console.error('Failed to load analysis types:', error); this.codebooksError = 'Failed to load codebooks'; checkDone(); }
    });
  }

  // ============================================
  // CREDIT BLOCKS
  // ============================================

  loadCredits(): void {
    this.loading = true;
    this.errorMessage = '';

    this.agremoService.listCredits().subscribe({
      next: (response) => {
        const allCredits = response.credits || [];
        const now = new Date();

        this.activeCredits = allCredits.filter(c =>
          !c.archivedAt && new Date(c.expiresAt) > now
        );
        this.archivedCredits = allCredits.filter(c =>
          c.archivedAt || new Date(c.expiresAt) <= now
        );

        // Pre-2026-05-20 Hub builds don't return these fields — fall back
        // to client-side computation so the admin works against either.
        this.availableAcres = response.availableAcres
          ?? this.computeAvailableAcres(this.activeCredits);
        this.availableHectares = response.availableHectares
          ?? (this.availableAcres / ACRES_PER_HECTARE);
        this.availableAnalyses = response.availableAnalyses
          ?? this.activeCredits
            .filter(c => c.creditType === 'analysis')
            .reduce((sum, c) => sum + c.remainingCredits, 0);

        this.loading = false;
      },
      error: (error) => {
        console.error('Failed to load credit blocks:', error);
        this.errorMessage = 'Failed to load credit blocks';
        this.loading = false;
      }
    });
  }

  /** Client-side acres-available fallback when Hub doesn't surface it. */
  private computeAvailableAcres(blocks: AgremoCreditBlock[]): number {
    return blocks.reduce((sum, b) => {
      if (b.creditType === 'acres') return sum + b.remainingCredits;
      if (b.creditType === 'hectares') return sum + b.remainingCredits * ACRES_PER_HECTARE;
      return sum; // 'analysis' blocks are not acre-convertible
    }, 0);
  }

  // ============================================
  // ADD CREDIT BLOCK
  // ============================================

  toggleAddForm(): void {
    this.showAddForm = !this.showAddForm;
    if (!this.showAddForm) {
      this.resetAddForm();
    }
  }

  submitNewCredit(): void {
    if (!this.newCreditAmount || this.newCreditAmount <= 0 || !this.newCreditExpiration) {
      return;
    }

    this.addingCredit = true;
    this.errorMessage = '';

    const amount = this.newCreditAmount;
    const creditType = this.newCreditType;

    this.agremoService.createCredit({
      amount,
      creditType,
      expiresAt: new Date(this.newCreditExpiration).toISOString()
    }).subscribe({
      next: () => {
        const unitLabel = creditType === 'hectares' ? 'hectares' : 'acres';
        this.successMessage = `Credit block of ${amount} ${unitLabel} created successfully`;
        this.resetAddForm();
        this.showAddForm = false;
        this.addingCredit = false;
        this.loadCredits();
        this.loadUsageStats();
        this.clearSuccessMessage();
      },
      error: (error) => {
        console.error('Failed to create credit block:', error);
        this.errorMessage = 'Failed to create credit block';
        this.addingCredit = false;
      }
    });
  }

  private resetAddForm(): void {
    this.newCreditAmount = null;
    this.newCreditType = 'acres';
    this.newCreditExpiration = '';
  }

  // ============================================
  // ARCHIVE CREDIT BLOCK
  // ============================================

  confirmArchive(credit: AgremoCreditBlock): void {
    this.archiveTargetId = credit.id;
    this.archiveTargetCredits = credit;
    this.showConfirmModal = true;
  }

  onConfirmArchive(): void {
    if (!this.archiveTargetId) return;

    this.confirmModalProcessing = true;

    this.agremoService.archiveCredit(this.archiveTargetId).subscribe({
      next: () => {
        this.successMessage = 'Credit block archived successfully';
        this.showConfirmModal = false;
        this.confirmModalProcessing = false;
        this.archiveTargetId = null;
        this.archiveTargetCredits = null;
        this.loadCredits();
        this.loadUsageStats();
        this.clearSuccessMessage();
      },
      error: (error) => {
        console.error('Failed to archive credit block:', error);
        this.errorMessage = 'Failed to archive credit block';
        this.confirmModalProcessing = false;
        this.showConfirmModal = false;
      }
    });
  }

  onCancelArchive(): void {
    this.showConfirmModal = false;
    this.confirmModalProcessing = false;
    this.archiveTargetId = null;
    this.archiveTargetCredits = null;
  }

  // ============================================
  // USAGE STATISTICS
  // ============================================

  loadUsageStats(): void {
    this.usageLoading = true;

    this.agremoService.getUsageStats().subscribe({
      next: (stats) => {
        this.usageStats = stats;
        this.usageLoading = false;
      },
      error: (error) => {
        console.error('Failed to load usage statistics:', error);
        this.usageLoading = false;
      }
    });
  }

  // ============================================
  // PROCESS PENDING
  // ============================================

  confirmProcessPending(): void {
    this.showProcessConfirmModal = true;
  }

  onConfirmProcessPending(): void {
    this.processingPending = true;
    this.showProcessConfirmModal = false;

    this.agremoService.processPending().subscribe({
      next: (result: AgremoProcessPendingResult) => {
        this.successMessage = `Processed ${result.processed} pending analyses. ${result.remaining} remaining.`;
        if (result.failed > 0) {
          this.successMessage += ` ${result.failed} failed.`;
        }
        this.processingPending = false;
        this.loadCredits();
        this.loadUsageStats();
        this.clearSuccessMessage();
      },
      error: (error) => {
        console.error('Failed to process pending analyses:', error);
        this.errorMessage = 'Failed to process pending analyses';
        this.processingPending = false;
      }
    });
  }

  onCancelProcessPending(): void {
    this.showProcessConfirmModal = false;
  }

  // ============================================
  // HELPERS
  // ============================================

  get totalAvailableCredits(): number {
    return this.activeCredits.reduce((sum, c) => sum + c.remainingCredits, 0);
  }

  get totalUsedCredits(): number {
    return this.activeCredits.reduce((sum, c) => sum + (c.totalCredits - c.remainingCredits), 0);
  }

  /**
   * Dual-unit display helpers — Agremo's dashboard shows hectares but the
   * platform consumes in acres, so we surface both to make reconciliation
   * easy. Both formatters use `maximumFractionDigits: 2` (no minimum), so
   * whole numbers render without trailing zeros and fractional values get
   * 2 decimals.
   */
  formatAcres(acres: number): string {
    if (acres === 0) return '0';
    return acres.toLocaleString('en-US', { maximumFractionDigits: 2 });
  }

  formatHectares(hectares: number): string {
    if (hectares === 0) return '0';
    return hectares.toLocaleString('en-US', { maximumFractionDigits: 2 });
  }

  /** Convert a block's `remainingCredits` value into acres for display. */
  blockRemainingAsAcres(block: AgremoCreditBlock): number {
    if (block.creditType === 'hectares') return block.remainingCredits * ACRES_PER_HECTARE;
    if (block.creditType === 'acres') return block.remainingCredits;
    return 0; // 'analysis' blocks don't convert to acres
  }

  /** Convert a block's `remainingCredits` value into hectares for display. */
  blockRemainingAsHectares(block: AgremoCreditBlock): number {
    if (block.creditType === 'hectares') return block.remainingCredits;
    if (block.creditType === 'acres') return block.remainingCredits / ACRES_PER_HECTARE;
    return 0;
  }

  /**
   * Human-readable label for a block's unit. Defaults to 'units' when the
   * server returns an unrecognized or missing creditType (e.g., during a
   * pre/post-migration rollout window or if a future credit type is
   * surfaced before the admin UI knows about it) so the UI degrades to a
   * safe generic label instead of rendering `undefined` in the table.
   */
  unitLabel(block: AgremoCreditBlock): string {
    switch (block.creditType) {
      case 'acres': return 'acres';
      case 'hectares': return 'ha';
      case 'analysis': return 'analyses';
      default: return 'units';
    }
  }

  getUsagePercent(credit: AgremoCreditBlock): number {
    if (credit.totalCredits === 0) return 0;
    return Math.round(((credit.totalCredits - credit.remainingCredits) / credit.totalCredits) * 100);
  }

  getUsageBarClass(credit: AgremoCreditBlock): string {
    const percent = this.getUsagePercent(credit);
    if (percent >= 90) return 'bg-danger';
    if (percent >= 70) return 'bg-warning';
    return 'bg-success';
  }

  isExpiringSoon(credit: AgremoCreditBlock): boolean {
    const expiresAt = new Date(credit.expiresAt);
    const sevenDaysFromNow = new Date();
    sevenDaysFromNow.setDate(sevenDaysFromNow.getDate() + 7);
    return expiresAt <= sevenDaysFromNow && expiresAt > new Date();
  }

  formatDate(dateString: string | null): string {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }

  formatDateTime(dateString: string | null): string {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  private clearSuccessMessage(): void {
    setTimeout(() => {
      this.successMessage = '';
    }, 5000);
  }
}
